import { z } from 'zod/v4';

/* ─────────────────── Aggregated schema-arrays imported from each namespace ─────────────────── */
import {
  authenticationNotificationParamSchemas,
  authenticationRequestParamSchemas,
  authenticationResponseParamSchemas,
} from './authentication';
import type { InfinexMessageNamespace, PassthroughNamespace } from './base';
/* Passthrough namespaces still come in directly */
import {
  eip1193NotificationParamsSchema,
  eip1193RequestParamsSchema,
  eip1193ResponseParamsSchema,
} from './eip1193';
import {
  handshakeNotificationParamSchemas,
  handshakeRequestParamSchemas,
  handshakeResponseParamSchemas,
} from './handshake';
import {
  heartbeatNotificationParamSchemas,
  heartbeatRequestParamSchemas,
  heartbeatResponseParamSchemas,
} from './heartbeat';
import {
  infinexOpsNotificationParamSchemas,
  infinexOpsRequestParamSchemas,
  infinexOpsResponseParamSchemas,
} from './infinexOps';
import {
  infinexUiNotificationParamSchemas,
  infinexUiRequestParamSchemas,
  infinexUiResponseParamSchemas,
} from './infinexUi';
import {
  popupInfoNotificationParamSchemas,
  popupInfoRequestParamSchemas,
  popupInfoResponseParamSchemas,
} from './popupInfo';
import { serverResponseNotificationParamSchemas } from './serverResponse';
import {
  sessionChangeNotificationParamSchemas,
  sessionChangeRequestParamSchemas,
  sessionChangeResponseParamSchemas,
} from './sessionChange';
import {
  solanaWalletNotificationParamsSchema,
  solanaWalletRequestParamsSchema,
  solanaWalletResponseParamsSchema,
} from './solanaWallet';
import {
  storedDataRequestParamSchemas,
  storedDataResponseParamSchemas,
} from './storedData';

/* ──────────────────────────────── Request-Params Schemas ──────────────────────────────── */
const eventRequestParamSchemas = [
  ...handshakeRequestParamSchemas,
  ...heartbeatRequestParamSchemas,
  ...infinexOpsRequestParamSchemas,
  ...authenticationRequestParamSchemas,
  ...sessionChangeRequestParamSchemas,
  ...storedDataRequestParamSchemas,
  ...infinexUiRequestParamSchemas,
  ...popupInfoRequestParamSchemas,
] as const;

/* ─────────────────────────────── Response-Params Schemas ─────────────────────────────── */
const eventResponseParamSchemas = [
  ...handshakeResponseParamSchemas,
  ...heartbeatResponseParamSchemas,
  ...infinexOpsResponseParamSchemas,
  ...authenticationResponseParamSchemas,
  ...sessionChangeResponseParamSchemas,
  ...storedDataResponseParamSchemas,
  ...infinexUiResponseParamSchemas,
  ...popupInfoResponseParamSchemas,
] as const;

/* ──────────────────────────── Notification-Params Schemas ────────────────────────────── */
const eventNotificationParamSchemas = [
  ...handshakeNotificationParamSchemas,
  ...authenticationNotificationParamSchemas,
  ...sessionChangeNotificationParamSchemas,
  ...infinexUiNotificationParamSchemas,
  ...serverResponseNotificationParamSchemas,
  ...heartbeatNotificationParamSchemas,
  ...infinexOpsNotificationParamSchemas,
  ...popupInfoNotificationParamSchemas,
] as const;

/* ──────────────────────────── Build discriminated unions ─────────────────────────── */
export const infinexRequestParamsSchema = z.union([
  z.discriminatedUnion('method', eventRequestParamSchemas),
  eip1193RequestParamsSchema,
  solanaWalletRequestParamsSchema,
]);

export const infinexResponseParamsSchema = z.union([
  z.discriminatedUnion('method', eventResponseParamSchemas),
  eip1193ResponseParamsSchema,
  solanaWalletResponseParamsSchema,
]);

export const infinexNotificationParamsSchema = z.union([
  z.discriminatedUnion('method', eventNotificationParamSchemas),
  eip1193NotificationParamsSchema,
  solanaWalletNotificationParamsSchema,
]);

/**
 * ─────────────────────────── Exported TypeScript types ───────────────────────────
 */
export type RequestEventParams = z.infer<typeof infinexRequestParamsSchema>;
export type ResponseEventParams = z.infer<typeof infinexResponseParamsSchema>;
export type NotificationEventParams = z.infer<
  typeof infinexNotificationParamsSchema
>;

export type MethodFor<N extends InfinexMessageNamespace> =
  // Passthrough namespaces → *any* method string
  N extends PassthroughNamespace
    ? string
    : // All other namespaces keep the previous behaviour
        | Extract<RequestEventParams, { namespace: N }>['method']
        | Extract<ResponseEventParams, { namespace: N }>['method']
        | Extract<NotificationEventParams, { namespace: N }>['method'];

type ExtractRequest<N, M> = Extract<
  RequestEventParams,
  {
    namespace: N;
    method: M;
  }
>;
type ExtractResponse<N, M> = Extract<
  ResponseEventParams,
  {
    namespace: N;
    method: M;
  }
>;
type ExtractNotification<N, M> = Extract<
  NotificationEventParams,
  {
    namespace: N;
    method: M;
  }
>;

export type RequestDataFor<
  N extends InfinexMessageNamespace,
  M extends MethodFor<N>,
> =
  // passthrough → any
  N extends PassthroughNamespace
    ? any
    : // otherwise pick the specific data shape (or fallback to never)
      ExtractRequest<N, M> extends { data: infer D }
      ? D
      : never;

export type ResponseDataFor<
  N extends InfinexMessageNamespace,
  M extends MethodFor<N>,
> = N extends PassthroughNamespace
  ? any
  : ExtractResponse<N, M> extends { response: infer R }
    ? R
    : never;

export type NotificationDataFor<
  N extends InfinexMessageNamespace,
  M extends MethodFor<N>,
> = N extends PassthroughNamespace
  ? any
  : ExtractNotification<N, M> extends { data: infer D }
    ? D
    : never;

export type ResponseFromRequest<P extends RequestEventParams> =
  // passthrough → any
  P['namespace'] extends PassthroughNamespace
    ? any
    : P extends { namespace: infer N; method: infer M }
      ? N extends InfinexMessageNamespace
        ? M extends MethodFor<N>
          ? Exclude<ResponseDataFor<N, M>, null>
          : never
        : never
      : never;
